import React, { useState, useEffect, useRef } from 'react';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Close, InfoOutlined, PhoneAndroid, Send, WarningAmber } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Trade, ViewMode } from '../../../../types/trades';

type TradeGroupTimerProps = {
    group: Trade[];
    groupKey: string;
    mode: ViewMode;
    isExtensionInstalled: boolean;
    autoCancelTimeLeftMs: number;
    cancelTimeLeftMs: number;
    isTimerExpired: boolean;
    isFreeCancel: boolean;
    isWaitingConfirm: boolean;
    isWaitingSend: boolean;
    isWaitingAccept: boolean;
    isWaitingVerification: boolean;
    sellerLoginSecureVerificationRequired?: boolean;
    buyerLoginSecureVerificationRequired?: boolean;
    isLoading: boolean;
    onBatchAccept: (groupKey: string, group: Trade[]) => void;
    onBatchSend: (groupKey: string, group: Trade[]) => void;
    onOpenMobileConfirmation: (groupKey: string, group: Trade[]) => void | Promise<void>;
    onCancelTarget: (group: Trade[]) => void;
    formatMs: (ms: number, showHours?: boolean) => string;
};

const TradeGroupActionPanel: React.FC<TradeGroupTimerProps> = ({
                                                                   group,
                                                                   groupKey,
                                                                   mode,
                                                                   isExtensionInstalled,
                                                                   autoCancelTimeLeftMs,
                                                                   cancelTimeLeftMs,
                                                                   isTimerExpired,
                                                                   isFreeCancel,
                                                                   isWaitingConfirm,
                                                                   isWaitingSend,
                                                                   isWaitingAccept,
                                                                   isWaitingVerification,
                                                                   sellerLoginSecureVerificationRequired = false,
                                                                   buyerLoginSecureVerificationRequired = false,
                                                                   isLoading,
                                                                   onBatchAccept,
                                                                   onBatchSend,
                                                                   onOpenMobileConfirmation,
                                                                   onCancelTarget,
                                                                   formatMs
                                                               }) => {
    const { t } = useTranslation();
    const isSeller = mode === 'sold';
    const isBuyer = mode === 'purchased';

    const [cooldownLeft, setCooldownLeft] = useState(0);
    const prevLoadingRef = useRef(isLoading);

    /** Aggregate state check for seller waiting on buyer or system verification */
    const isWaitingVerificationOrAccept =
        isWaitingAccept ||
        isWaitingVerification ||
        sellerLoginSecureVerificationRequired ||
        buyerLoginSecureVerificationRequired;

    const mobileConfirmationTargets = group.filter(
        trade =>
            trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_SEND' &&
            trade.effectiveSteamTradeOfferStatus === 'CREATEDNEEDSConfirmation'
    );

    const showMobileConfirmationAction = isSeller && mobileConfirmationTargets.length > 0;

    const handleOpenMobileConfirmation = async () => {
        if (isLoading || mobileConfirmationTargets.length === 0) {
            return;
        }

        await onOpenMobileConfirmation(groupKey, mobileConfirmationTargets);
    };

    /** Detect completion of loading state to start cooldown */
    useEffect(() => {
        if (prevLoadingRef.current && !isLoading) {
            setCooldownLeft(10);
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    /** Countdown timer for seller retry cooldown */
    useEffect(() => {
        if (cooldownLeft <= 0) return;
        const timer = setTimeout(() => {
            setCooldownLeft((prev) => prev - 1);
        }, 1000);
        return () => clearTimeout(timer);
    }, [cooldownLeft]);

    const handleBatchSendWithCooldown = () => {
        if (cooldownLeft > 0 || isLoading) return;
        onBatchSend(groupKey, group);
    };

    return (
        <div className="p-4 bg-gray-50 dark:bg-neutral-900/50 rounded-lg flex justify-between items-center border border-gray-100 dark:border-white/5">
            <div className="w-1/3">
                <p className="font-black text-gray-800 dark:text-gray-100 text-[10px] uppercase tracking-wider">
                    {isSeller ? (
                        isWaitingConfirm
                            ? t('history.actions.action_required')
                            : showMobileConfirmationAction
                                ? t('history.actions.action_required')
                                : isWaitingVerificationOrAccept
                                    ? t('history.actions.waiting_acceptance', { defaultValue: 'Waiting' })
                                    : t('history.actions.send_required')
                    ) : (
                        t('history.actions.seller_notified')
                    )}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 font-medium leading-tight">
                    {isSeller ? (
                        isWaitingConfirm
                            ? t('history.actions.confirm_proceed')
                            : showMobileConfirmationAction
                                ? 'Confirm the Steam trade on your mobile device'
                                : isWaitingVerificationOrAccept
                                    ? t('history.actions.waiting_acceptance_desc', { defaultValue: 'Waiting for partner or system verification' })
                                    : t('history.actions.open_steam_send')
                    ) : (
                        isWaitingConfirm
                            ? t('history.actions.waiting_acceptance')
                            : t('history.actions.waiting_delivery')
                    )}
                </p>
            </div>

            <div className="flex flex-col items-center flex-1 border-l border-r border-gray-200 dark:border-white/5 px-2">
                <span className="text-[9px] text-gray-400 dark:text-gray-500 uppercase font-black tracking-tighter">
                    {t('history.actions.expires_in')}
                </span>

                <span className={`text-sm font-mono font-black ${isTimerExpired || autoCancelTimeLeftMs < 600000 ? 'text-red-500 animate-pulse' : 'text-gray-800 dark:text-gray-200'}`}>
                    {formatMs(autoCancelTimeLeftMs, true)}
                </span>
            </div>

            <div className="w-1/3 flex flex-col items-end pl-4 relative">
                {isSeller && (!isTimerExpired || showMobileConfirmationAction) && !isWaitingVerificationOrAccept && (
                    <div className="flex flex-col items-end w-full min-w-[160px] max-w-[220px]">
                        <div className="flex items-center gap-2 w-full">
                            <Tooltip title={!isExtensionInstalled ? t('history.tooltips.extension_required') : ''} arrow placement="top">
                                <span className="flex-1">
                                    {isWaitingConfirm ? (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={!isExtensionInstalled || isLoading}
                                            className="!text-white !font-bold w-full"
                                            onClick={() => onBatchAccept(groupKey, group)}
                                            sx={{ height: '38px', borderRadius: '8px', boxShadow: 'none' }}
                                        >
                                            {isLoading ? <CircularProgress size={20} color="inherit" /> : t('history.actions.accept_sale')}
                                        </Button>
                                    ) : showMobileConfirmationAction ? (
                                        <Tooltip title="Confirm trade on Steam Mobile" arrow>
                                            <span className="flex justify-end">
                                                <Button
                                                    onClick={handleOpenMobileConfirmation}
                                                    disabled={isLoading}
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PhoneAndroid fontSize="small" />}
                                                    className="!text-white !font-bold w-full"
                                                    sx={{ height: '38px', borderRadius: '8px', boxShadow: 'none', textTransform: 'none' }}
                                                >
                                                    {isLoading ? 'Loading...' : 'Confirm Trade'}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            disabled={!isExtensionInstalled || isLoading || cooldownLeft > 0}
                                            className="!text-white !font-bold w-full"
                                            onClick={handleBatchSendWithCooldown}
                                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send fontSize="small" />}
                                            sx={{ height: '38px', borderRadius: '8px', boxShadow: 'none' }}
                                        >
                                            {isLoading
                                                ? t('history.actions.sending')
                                                : cooldownLeft > 0
                                                    ? `${t('history.actions.retry_in', { defaultValue: 'Retry in' })} (${cooldownLeft}s)`
                                                    : t('history.actions.send_item')}
                                        </Button>
                                    )}
                                </span>
                            </Tooltip>

                            <IconButton
                                onClick={() => onCancelTarget(group)}
                                color="error"
                                disabled={isLoading}
                                className="!bg-red-500 !text-white hover:!bg-red-600 !flex-shrink-0"
                                size="small"
                                sx={{ borderRadius: '8px', height: '38px', width: '38px' }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </div>

                        {!showMobileConfirmationAction && (
                            <div className="absolute top-[31px] right-0 w-full flex justify-end">
                                <Tooltip title={t('history.tooltips.seller_penalty_info')} arrow>
                                    <p className="text-[9px] text-red-500 dark:text-red-400 font-black uppercase tracking-tighter flex items-center mt-1 whitespace-nowrap">
                                        <WarningAmber className="!w-3 mr-1" />
                                        {t('history.actions.penalty_risk')}
                                    </p>
                                </Tooltip>
                            </div>
                        )}
                    </div>
                )}

                {isSeller && isTimerExpired && !showMobileConfirmationAction && !isWaitingVerificationOrAccept && (
                    <span className="text-[10px] font-black text-red-500 uppercase tracking-widest animate-pulse">
                        {t('history.status.processing_expiration')}
                    </span>
                )}

                {isBuyer && (
                    <div className="flex flex-col items-end w-full max-w-[160px]">
                        <Button
                            variant="contained"
                            color={isFreeCancel ? 'error' : 'warning'}
                            size="small"
                            onClick={() => onCancelTarget(group)}
                            className="!text-white !font-bold w-full"
                            sx={{ height: 38, borderRadius: '8px', boxShadow: 'none' }}
                        >
                            {t('history.actions.cancel_trade')}
                        </Button>

                        <div className="absolute top-[38px] right-0 w-full flex justify-end">
                            <p className="text-[9px] text-right leading-tight whitespace-nowrap flex items-center mt-1">
                                {isFreeCancel ? (
                                    <span className="text-red-500 dark:text-neutral-400 font-black uppercase tracking-tighter flex items-center">
                                        <InfoOutlined className="!w-3 mr-1" />
                                        {t('history.actions.free_cancellation')}
                                    </span>
                                ) : (
                                    <span className="text-gray-400 font-medium">
                                        {t('history.actions.fee_info', { time: formatMs(cancelTimeLeftMs, false) })}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TradeGroupActionPanel;